import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import ProjectCard from "@/components/ProjectCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, SlidersHorizontal, X, ArrowUpDown } from "lucide-react";

const CATEGORIES = ["all", "Programación", "Diseño", "Redacción", "Marketing", "Video & Audio", "IA & Datos"];
const SORT_OPTIONS = [
  { value: "newest", label: "Más recientes" },
  { value: "oldest", label: "Más antiguos" },
  { value: "budget_high", label: "Mayor presupuesto" },
  { value: "budget_low", label: "Menor presupuesto" },
];
const BUDGET_MIN = 0;
const BUDGET_MAX = 500;

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [allSkills, setAllSkills] = useState([]);

  const category = params.get("category") || "all";
  const sort = params.get("sort") || "newest";
  const selectedSkills = useMemo(
    () => (params.get("skills") ? params.get("skills").split(",") : []),
    [params]
  );
  const minBudget = parseInt(params.get("min_budget") || String(BUDGET_MIN), 10);
  const maxBudget = parseInt(params.get("max_budget") || String(BUDGET_MAX), 10);
  const [range, setRange] = useState([minBudget, maxBudget]);

  useEffect(() => {
    setRange([minBudget, maxBudget]);
  }, [minBudget, maxBudget]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects/skills/all");
        setAllSkills(data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const query = { sort };
        if (category && category !== "all") query.category = category;
        if (q) query.q = q;
        if (selectedSkills.length) query.skills = selectedSkills.join(",");
        if (minBudget > BUDGET_MIN) query.min_budget = minBudget;
        if (maxBudget < BUDGET_MAX) query.max_budget = maxBudget;
        const { data } = await api.get("/projects", { params: query });
        setProjects(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, q, sort, selectedSkills, minBudget, maxBudget]);

  const patchParams = (patches) => {
    const next = new URLSearchParams(params);
    Object.entries(patches).forEach(([k, v]) => {
      if (v === null || v === "" || v === undefined) next.delete(k);
      else next.set(k, v);
    });
    setParams(next);
  };

  const setCategory = (c) => patchParams({ category: c === "all" ? null : c });
  const setSort = (s) => patchParams({ sort: s === "newest" ? null : s });

  const toggleSkill = (skill) => {
    const next = selectedSkills.includes(skill)
      ? selectedSkills.filter((s) => s !== skill)
      : [...selectedSkills, skill];
    patchParams({ skills: next.length ? next.join(",") : null });
  };

  const applyBudget = () => {
    patchParams({
      min_budget: range[0] > BUDGET_MIN ? String(range[0]) : null,
      max_budget: range[1] < BUDGET_MAX ? String(range[1]) : null,
    });
  };

  const clearAllFilters = () => {
    setQ("");
    setRange([BUDGET_MIN, BUDGET_MAX]);
    setParams(new URLSearchParams());
  };

  const hasActiveFilters =
    (category && category !== "all") ||
    q ||
    selectedSkills.length > 0 ||
    minBudget > BUDGET_MIN ||
    maxBudget < BUDGET_MAX ||
    (sort && sort !== "newest");

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="mb-10">
          <div className="text-xs tracking-widest uppercase text-primary font-bold mb-2">Explorar</div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">Proyectos disponibles</h1>
        </div>

        {/* Search + sort */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por título o descripción…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 h-11"
              data-testid="projects-search-input"
            />
          </div>

          {/* Sort */}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 min-w-[190px] w-auto" data-testid="sort-select">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 opacity-70" />
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`sort-option-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Skills dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-11 rounded-md" data-testid="skills-filter-btn">
                <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" /> Habilidades
                {selectedSkills.length > 0 && (
                  <span className="ml-2 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {selectedSkills.length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
              <DropdownMenuLabel className="text-xs tracking-widest uppercase font-bold text-muted-foreground">
                Filtrar por habilidad
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allSkills.length === 0 ? (
                <div className="text-sm text-muted-foreground px-2 py-3 text-center">Sin habilidades</div>
              ) : (
                allSkills.map((s) => (
                  <DropdownMenuCheckboxItem
                    key={s}
                    checked={selectedSkills.includes(s)}
                    onCheckedChange={() => toggleSkill(s)}
                    onSelect={(e) => e.preventDefault()}
                    data-testid={`skill-filter-${s}`}
                  >
                    {s}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Budget range */}
        <div className="mb-8 p-5 rounded-lg border border-border bg-card">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs tracking-widest uppercase font-bold text-muted-foreground">
              Presupuesto (USD)
            </div>
            <div className="text-sm font-medium tabular-nums" data-testid="budget-range-display">
              ${range[0]} — ${range[1]}{range[1] >= BUDGET_MAX ? "+" : ""}
            </div>
          </div>
          <Slider
            min={BUDGET_MIN}
            max={BUDGET_MAX}
            step={10}
            value={range}
            onValueChange={setRange}
            onValueCommit={applyBudget}
            className="mb-2"
            data-testid="budget-slider"
          />
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>${BUDGET_MIN}</span>
            <span>${BUDGET_MAX}+</span>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`filter-${c}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                category === c
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:bg-muted"
              }`}
            >
              {c === "all" ? "Todas" : c}
            </button>
          ))}
        </div>

        {/* Active filter chips + clear */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {selectedSkills.map((s) => (
              <span key={s} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-accent text-accent-foreground">
                {s}
                <button onClick={() => toggleSkill(s)} aria-label={`Quitar ${s}`} data-testid={`chip-remove-${s}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            <button
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-primary transition-colors underline underline-offset-2"
              data-testid="clear-filters-btn"
            >
              Limpiar filtros
            </button>
          </div>
        )}

        {/* Results count */}
        <div className="text-sm text-muted-foreground mb-4" data-testid="results-count">
          {loading ? "Cargando…" : `${projects.length} proyecto${projects.length === 1 ? "" : "s"}`}
        </div>

        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Cargando…</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-lg">
            <p className="text-muted-foreground mb-3">No hay proyectos que coincidan con tu búsqueda.</p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearAllFilters} className="rounded-full" data-testid="empty-clear-btn">
                Limpiar filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
            {projects.map((p, i) => (
              <div key={p.project_id} style={{ animationDelay: `${i * 40}ms` }} className="animate-fade-in-up">
                <ProjectCard project={p} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
